/** Half-width (Latin, digits, ASCII punctuation) vs full-width (CJK, etc.) for title badge sizing. */

function isHalfWidthChar(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  if (code <= 0x007f) return true;
  if (code >= 0xff61 && code <= 0xff9f) return true;
  return false;
}

function isFullWidthChar(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  if (code >= 0x4e00 && code <= 0x9fff) return true;
  if (code >= 0x3400 && code <= 0x4dbf) return true;
  if (code >= 0xf900 && code <= 0xfaff) return true;
  if (code >= 0x3040 && code <= 0x30ff) return true;
  if (code >= 0xac00 && code <= 0xd7af) return true;
  if (code >= 0xff01 && code <= 0xffee) return true;
  if (code === 0x3000) return true;
  if (code >= 0x3001 && code <= 0x303f) return true;
  return false;
}

/** Display width in "em" units (1 = one CJK cell, 0.5 = ASCII). */
export function measureTableTitleTextWidth(text: string): number {
  let units = 0;
  for (const char of text) {
    if (isHalfWidthChar(char)) {
      units += 0.5;
    } else if (isFullWidthChar(char)) {
      units += 1;
    } else {
      units += 0.75;
    }
  }
  return units;
}

export type TableTitleBackgroundWidthOptions = {
  fontSize?: number;
  minWidth?: number;
  maxWidth?: number;
  /** Horizontal padding inside the title badge image (px). */
  paddingPx?: number;
  /** Per-unit width multiplier (default 0.92). */
  unitWidthFactor?: number;
  /** Final width scale, e.g. 0.88 for tighter mobile badge. */
  scale?: number;
};

/**
 * Width for `table_title.png` background behind `[ title ]`.
 * Uses mixed CJK / ASCII width instead of raw string length.
 */
export function getTableTitleBackgroundWidth(
  title: string | undefined,
  options: TableTitleBackgroundWidthOptions = {},
): number {
  if (!title) return 0;

  const {
    fontSize = 16,
    minWidth = 112,
    maxWidth = 300,
    paddingPx = 28,
    unitWidthFactor = 0.92,
    scale = 1,
  } = options;

  const displayText = `[ ${title} ]`;
  const units = measureTableTitleTextWidth(displayText);
  const pxPerUnit = fontSize * unitWidthFactor;
  const width = Math.ceil((units * pxPerUnit + paddingPx) * scale);

  return Math.min(maxWidth, Math.max(minWidth, width));
}
